import React, { useEffect, useState, useCallback } from "react";
import { api, apiError } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { Hash, Sparkles, Copy, Check, RefreshCw, AlertCircle, Wand2 } from "lucide-react";

const REFRESH_LABEL = "Rigenera evergreen";

export default function CaptionHashtagCard({ refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [seedGenerating, setSeedGenerating] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [seed, setSeed] = useState("");

  const load = useCallback(async (forceRefresh = false) => {
    if (forceRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      const url = forceRefresh
        ? "/client/caption-hashtags?refresh=1"
        : "/client/caption-hashtags";
      const { data: res } = await api.get(url);
      setData(res);
      setSelectedTags([]);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const generateFromSeed = async () => {
    const trimmed = seed.trim();
    if (!trimmed) {
      toast.info("Scrivi prima una parola o una frase da cui partire");
      return;
    }
    setSeedGenerating(true);
    try {
      const { data: res } = await api.get(
        `/client/caption-hashtags?seed=${encodeURIComponent(trimmed)}`,
      );
      setData(res);
      setSelectedTags([]);
      toast.success("Caption generata dal tuo spunto ✨");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSeedGenerating(false);
    }
  };

  const copy = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      toast.success("Copiato negli appunti");
      setTimeout(() => setCopiedKey(null), 2000);
    } catch {
      toast.error("Copia manuale non disponibile");
    }
  };

  const toggleTag = (tag) => {
    setSelectedTags((prev) => {
      if (prev.includes(tag)) return prev.filter((t) => t !== tag);
      if (prev.length >= 5) {
        toast.info("Massimo 5 hashtag selezionati");
        return prev;
      }
      return [...prev, tag];
    });
  };

  const copySelectedTags = async () => {
    if (selectedTags.length === 0) {
      toast.info("Seleziona almeno un hashtag toccandolo");
      return;
    }
    await copy("selected-tags", selectedTags.join(" "));
  };

  const seedQuota = data?.seed_quota;
  const seedRemaining = seedQuota?.remaining ?? 1;
  const seedBudgetLeft = seedRemaining > 0;

  return (
    <section
      data-testid="caption-hashtag-card"
      className="rounded-3xl border border-[var(--mp-ink)] bg-white shadow-brutal-sm overflow-hidden"
    >
      <div className="px-6 py-5 sm:px-8 sm:py-6 bg-[var(--mp-yellow)] border-b border-[var(--mp-ink)] flex items-start gap-4 flex-wrap">
        <div className="h-10 w-10 rounded-full bg-[var(--mp-ink)] text-white grid place-items-center shrink-0">
          <Sparkles className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-[10px] font-bold uppercase tracking-[0.24em]">
              Strumento gratuito
            </div>
            <Badge className="rounded-full bg-[var(--mp-ink)] text-white text-[9px] uppercase tracking-[0.18em] px-2 py-0.5">
              AI
            </Badge>
          </div>
          <div className="font-display text-2xl sm:text-3xl tracking-tight mt-1">
            Caption + hashtag per il tuo salone
          </div>
          <p className="mt-1.5 text-[var(--mp-ink)]/80 text-sm max-w-2xl">
            L'AI genera una caption evergreen basata sul tuo posizionamento.
            Oppure scrivi una frase da cui partire (es. "balayage biondo caldo
            su cliente 30 anni") e ti costruisce caption + hashtag ad-hoc.
          </p>
        </div>
      </div>

      <div className="p-6 sm:p-8 space-y-6">
        {loading ? (
          <div data-testid="caption-loading" className="text-center py-10 text-[var(--mp-muted)]">
            <Sparkles className="h-6 w-6 mx-auto mb-3 animate-pulse" />
            Sto generando la tua caption e gli hashtag…
          </div>
        ) : data?.needs_positioning ? (
          <div
            data-testid="caption-needs-positioning"
            className="rounded-2xl border border-[var(--mp-ink)] bg-[var(--mp-dash-bg)] p-5 flex items-start gap-3"
          >
            <AlertCircle className="h-5 w-5 text-[var(--mp-ink)] shrink-0 mt-0.5" />
            <div className="text-sm">
              <strong>Compila prima il tuo posizionamento</strong> nella card sopra.
              Senza quel testo, l'AI non sa di cosa parlare. Bastano 2-3 righe.
            </div>
          </div>
        ) : data?.caption ? (
          <>
            {/* Seed input — visible only when positioning is complete */}
            <div
              data-testid="caption-seed-block"
              className="rounded-2xl bg-[var(--mp-yellow)]/40 border-2 border-dashed border-[var(--mp-ink)] p-4 space-y-3"
            >
              <div className="flex items-start gap-2 flex-wrap">
                <Wand2 className="h-4 w-4 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-bold uppercase tracking-[0.18em]">
                    Hai un post specifico? Parti da uno spunto
                  </div>
                  <p className="text-xs text-[var(--mp-muted)] mt-0.5">
                    Descrivi in una frase cosa hai fatto o cosa vuoi raccontare —
                    l'AI userà quello come argomento. <strong>1 generazione al giorno.</strong>
                  </p>
                </div>
                {seedQuota && (
                  <Badge
                    data-testid="caption-seed-quota-badge"
                    className={`rounded-full text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 border ${
                      seedBudgetLeft
                        ? "bg-white text-[var(--mp-ink)] border-[var(--mp-ink)]"
                        : "bg-[var(--mp-ink)] text-white border-[var(--mp-ink)]"
                    }`}
                  >
                    {seedQuota.used}/{seedQuota.max} oggi
                  </Badge>
                )}
              </div>
              <Textarea
                data-testid="caption-seed-input"
                value={seed}
                onChange={(e) => setSeed(e.target.value.slice(0, 200))}
                placeholder='es. "Balayage biondo caramello su capelli castani, look naturale per una 30enne"'
                rows={2}
                maxLength={200}
                disabled={seedGenerating || !seedBudgetLeft}
                className="rounded-xl border-[var(--mp-ink)] bg-white text-sm resize-none"
              />
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-[11px] text-[var(--mp-muted)]">
                  {seed.length}/200 · {seedBudgetLeft
                    ? "Ne resta 1 oggi"
                    : "Riprova domani (reset a mezzanotte)"}
                </div>
                <Button
                  data-testid="caption-seed-generate"
                  onClick={generateFromSeed}
                  disabled={seedGenerating || !seed.trim() || !seedBudgetLeft}
                  className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
                >
                  <Wand2 className={`h-4 w-4 mr-1.5 ${seedGenerating ? "animate-pulse" : ""}`} />
                  {seedGenerating ? "Genero dal tuo spunto…" : "Genera dal mio spunto"}
                </Button>
              </div>
            </div>

            {/* Caption */}
            <div data-testid="caption-block">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] flex items-center gap-2">
                  Caption suggerita
                  {data?.from_seed && (
                    <Badge
                      data-testid="caption-from-seed-badge"
                      className="rounded-full bg-[var(--mp-yellow)] text-[var(--mp-ink)] border border-[var(--mp-ink)] text-[9px] px-1.5 py-0"
                    >
                      DAL TUO SPUNTO
                    </Badge>
                  )}
                </div>
                <Button
                  size="sm"
                  data-testid="caption-copy"
                  onClick={() => copy("caption", data.caption)}
                  className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
                >
                  {copiedKey === "caption" ? (
                    <Check className="h-4 w-4 mr-1.5" />
                  ) : (
                    <Copy className="h-4 w-4 mr-1.5" />
                  )}
                  {copiedKey === "caption" ? "Copiata" : "Copia caption"}
                </Button>
              </div>
              <div className="rounded-2xl bg-[var(--mp-dash-bg)] border border-[var(--mp-border)] p-4 text-sm whitespace-pre-wrap leading-relaxed">
                {data.caption}
              </div>
            </div>

            {/* Hashtags */}
            <div data-testid="hashtag-block">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] flex items-center gap-1.5">
                  <Hash className="h-3 w-3" />
                  10 hashtag — scegline 5
                </div>
                {selectedTags.length > 0 && (
                  <Button
                    size="sm"
                    data-testid="hashtag-copy-selected"
                    onClick={copySelectedTags}
                    className="rounded-full bg-[var(--mp-yellow)] hover:bg-[var(--mp-yellow)] text-[var(--mp-ink)] border border-[var(--mp-ink)] font-bold"
                  >
                    {copiedKey === "selected-tags" ? (
                      <Check className="h-4 w-4 mr-1.5" />
                    ) : (
                      <Copy className="h-4 w-4 mr-1.5" />
                    )}
                    Copia {selectedTags.length} selezionati
                  </Button>
                )}
              </div>
              <p className="text-[12px] text-[var(--mp-muted)] mb-3">
                💡 Su Instagram il consiglio è di mettere <strong>massimo 5
                hashtag</strong> per post — tocca quelli che ti sembrano più
                pertinenti, poi copiali.
              </p>
              <div className="flex flex-wrap gap-1.5">
                {(data.hashtags || []).map((tag) => {
                  const selected = selectedTags.includes(tag);
                  return (
                    <button
                      key={tag}
                      type="button"
                      data-testid={`hashtag-pill-${tag}`}
                      onClick={() => toggleTag(tag)}
                      className={`inline-flex items-center gap-1 px-3 h-8 rounded-full border text-[13px] font-medium transition-all ${
                        selected
                          ? "bg-[var(--mp-ink)] text-white border-[var(--mp-ink)] shadow-brutal-sm -translate-y-0.5"
                          : "bg-white text-[var(--mp-ink)] border-[var(--mp-ink)] hover:-translate-y-0.5"
                      }`}
                      title={selected ? "Tocca per deselezionare" : "Tocca per selezionare"}
                    >
                      {selected && <Check className="h-3 w-3" />}
                      {tag}
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        ) : null}

        <div className="flex items-center justify-between flex-wrap gap-3 pt-2 border-t border-[var(--mp-border)]">
          <div className="text-[11px] text-[var(--mp-muted)]">
            {data?.from_seed
              ? "Generato dallo spunto che hai scritto."
              : data?.cached
              ? "Caption evergreen salvata di oggi. Premi Rigenera per un'alternativa."
              : data?.caption
              ? "Caption evergreen generata adesso."
              : ""}
          </div>
          <Button
            onClick={() => load(true)}
            disabled={refreshing || loading || data?.needs_positioning}
            variant="outline"
            data-testid="caption-regenerate"
            className="rounded-full border-[var(--mp-ink)]"
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`}
            />
            {refreshing ? "Genero…" : REFRESH_LABEL}
          </Button>
        </div>
      </div>
    </section>
  );
}
